import type { CashFlowRow, DisplayCurrency } from "@/hooks/finance/cash-flow/useCashFlow";
import {
  exportStyledExcel,
  type ExportRow,
  type RowKind,
} from "@/components/finance/reports/shared/exportStyledExcel";
import { exportStyledPdf } from "@/components/finance/reports/shared/exportStyledPdf";

type PeriodLabel = { key: string; label: string };

export const PNL_TITLE = "Profit & Loss Report";
const isPct = (r: CashFlowRow) => r.categoryId === "__pct__";

// Contribution / gross-profit lines highlighted in emerald on screen.
const CP_KEYS = new Set(["pnl-cp1", "pnl-cp2", "pnl-gross-profit"]);

function kindFor(r: CashFlowRow): RowKind {
  if (r.isHeader) return "header";
  if (isPct(r)) return "percent";
  if (r.key === "pnl-ebit") return "grandTotal";
  if (CP_KEYS.has(r.key)) return "highlight";
  if (r.isTotal) return "total";
  return "data";
}

/** Build the shared ExportRow model consumed by both the Excel and PDF exports. */
export function buildPnlExportRows(
  pnlRows: CashFlowRow[],
  unmappedRows: CashFlowRow[],
  periodLabels: PeriodLabel[]
): ExportRow[] {
  const source = [
    ...pnlRows,
    ...(unmappedRows.length
      ? ([
          {
            key: "pnl-unmapped-header",
            label: "UNMAPPED OPERATING POSITIONS",
            isHeader: true,
            values: new Map<string, number>(),
          } as CashFlowRow,
          ...unmappedRows,
        ] as CashFlowRow[])
      : []),
  ];

  return source.map((r) => {
    const kind = kindFor(r);
    return {
      label: r.label,
      indent: r.indent ?? 0,
      kind,
      cells:
        kind === "header"
          ? undefined
          : periodLabels.map((p) => ({
              value: r.values.get(p.key) ?? 0,
              format: isPct(r) ? ("percent" as const) : ("currency" as const),
            })),
    };
  });
}

export function exportPnlToExcel(
  pnlRows: CashFlowRow[],
  unmappedRows: CashFlowRow[],
  periodLabels: PeriodLabel[],
  displayCurrency: DisplayCurrency,
  fileName: string
) {
  return exportStyledExcel({
    title: PNL_TITLE,
    sheetName: "P&L",
    periodLabels,
    rows: buildPnlExportRows(pnlRows, unmappedRows, periodLabels),
    displayCurrency,
    fileName,
  });
}

export function exportPnlToPdf(
  pnlRows: CashFlowRow[],
  unmappedRows: CashFlowRow[],
  periodLabels: PeriodLabel[],
  displayCurrency: DisplayCurrency,
  fileName: string
) {
  return exportStyledPdf({
    title: PNL_TITLE,
    periodLabels,
    rows: buildPnlExportRows(pnlRows, unmappedRows, periodLabels),
    displayCurrency,
    fileName,
  });
}
